import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faStopwatch, faBell, faCalendar, faClock, faHourglassHalf, faGlobe, faMoon, faSun, faRobot, faCloudRain, faBolt, faCloud } from '@fortawesome/free-solid-svg-icons';
import './App.css';
import ClockComponent from './components/clock/ClockComponent';
import TimerComponent from './components/Timer/TimerComponent';
import StopwatchComponent from './components/StopWatch/StopwatchComponent';
import AlarmComponent from './components/Alarm/AlarmComponent';
import FocusTimerComponent from './components/focus/FocusTimerComponent';
import WorldClockComponent from './components/WorldClock/WorldClockComponent';
import SmartSchedulerComponent from './components/SmartSchiduler/SmartSchedulerComponent';
import AIPlannerComponent from './components/AIPlanner/AIPlannerComponent';  // Import AI Planner Component
import { ReactComponent as EarlyMorningSVG } from './assets/early-morning.svg';
import { ReactComponent as MorningSVG } from './assets/morning.svg';
import { ReactComponent as LateMorningSVG } from './assets/late-morning.svg';
import { ReactComponent as AfternoonSVG } from './assets/afternoon.svg';
import { ReactComponent as EveningSVG } from './assets/evening.svg';
import { ReactComponent as NightSVG } from './assets/night.svg';
import { ReactComponent as LateNightSVG } from './assets/late-night.svg';
import { ReactComponent as EarlyNightSVG } from './assets/early-night.svg';

function App() {
  const [, setCurrentTime] = useState(new Date());
  const [activeTab, setActiveTab] = useState("home");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [weatherData, setWeatherData] = useState({ temp: 'N/A', description: 'Unknown', icon: '', daily: [], location: 'Unknown' });
  const [digitalClock, setDigitalClock] = useState('');
  const svgRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      setDigitalClock(now.toLocaleTimeString());
    }, 1000);

    const updateBackgroundImage = () => {
      const dayOfWeek = new Date().getDay();
      switch (dayOfWeek) {
        case 0:
          setBackgroundImage("url('/assets/bg.jpg')"); // Sunday
          break;
        case 1:
          setBackgroundImage("url('/assets/bg1.jpg')"); // Monday
          break;
        case 2:
          setBackgroundImage("url('/assets/bg6.jpg')"); // Tuesday
          break;
        case 3:
          setBackgroundImage("url('/assets/bg3.jpg')"); // Wednesday
          break;
        case 4:
          setBackgroundImage("url('/assets/bg4.jpg')"); // Thursday
          break;
        case 5:
          setBackgroundImage("url('/assets/bg5.jpg')"); // Friday
          break;
        case 6:
          setBackgroundImage("url('/assets/bg6.jpg')"); // Saturday
          break;
        default:
          setBackgroundImage("url('/assets/bg1.jpg')"); // Default
          break;
      }
    };

    const fetchWeatherData = () => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = true;

      xhr.addEventListener('readystatechange', function () {
        if (this.readyState === this.DONE) {
          try {
            const data = JSON.parse(this.responseText);
            if (data && data.main && data.weather && data.name) {
              setWeatherData({
                temp: data.main.temp,
                description: data.weather[0].description,
                icon: data.weather[0].icon,
                daily: data.daily,
                location: data.name,  // Get the city name or location
              });
            } else {
              console.error('Invalid weather data structure:', data);
            }
          } catch (error) {
            console.error('Failed to parse weather data:', error);
          }
        }
      });

      xhr.open('GET', 'https://open-weather13.p.rapidapi.com/city/london/EN');
      xhr.setRequestHeader('x-rapidapi-key', '03b6372cddmsh44ece26aba85763p1d4e75jsn55112c398abc');
      xhr.setRequestHeader('x-rapidapi-host', 'open-weather13.p.rapidapi.com');

      xhr.send(null);
    };

    fetchWeatherData();
    updateBackgroundImage();

    const timeoutId = setTimeout(() => {
      if (svgRef.current) {
        svgRef.current.classList.add("animated");
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      clearTimeout(timeoutId);
    };
  }, []);

  const toggleSidebar = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const getWeatherIcon = () => {
    const hour = new Date().getHours();
    const isDaytime = hour >= 6 && hour < 18;
    const weatherDescription = weatherData.description.toLowerCase();

    if (weatherDescription.includes('rain')) {
      return <FontAwesomeIcon icon={faCloudRain} className="weather-icon rain-icon" />;
    } else if (weatherDescription.includes('storm')) {
      return <FontAwesomeIcon icon={faBolt} className="weather-icon storm-icon" />;
    } else if (weatherDescription.includes('cloud')) {
      return <FontAwesomeIcon icon={faCloud} className="weather-icon cloud-icon" />;
    } else if (weatherDescription.includes('sun') || weatherDescription.includes('clear')) {
      return isDaytime ? <FontAwesomeIcon icon={faSun} className="weather-icon sun-icon" /> : <FontAwesomeIcon icon={faMoon} className="weather-icon moon-icon" />;
    } else {
      return <FontAwesomeIcon icon={isDaytime ? faSun : faMoon} className={`weather-icon ${isDaytime ? 'sun-icon' : 'moon-icon'}`} />;
    }
  };

  const getCurrentSvg = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 8) {
      return <EarlyMorningSVG ref={svgRef} />;
    } else if (hour >= 8 && hour < 11) {
      return <MorningSVG ref={svgRef} />;
    } else if (hour >= 11 && hour < 14) {
      return <LateMorningSVG ref={svgRef} />;
    } else if (hour >= 14 && hour < 17) {
      return <AfternoonSVG ref={svgRef} />;
    } else if (hour >= 17 && hour < 20) {
      return <EveningSVG ref={svgRef} />;
    } else if (hour >= 20 && hour < 23) {
      return <EarlyNightSVG ref={svgRef} />;
    } else if (hour >= 23 || hour < 2) {
      return <LateNightSVG ref={svgRef} />;
    } else {
      return <NightSVG ref={svgRef} />;
    }
  };

  const renderHome = () => (
    <div className="home-tab">
      <ClockComponent />
    </div>
  );

  const renderTimer = () => (
    <div className="timer-tab">
      <TimerComponent />
    </div>
  );

  const renderStopwatch = () => (
    <div className="stopwatch-tab">
      <StopwatchComponent />
    </div>
  );

  const renderAlarm = () => (
    <div className="alarm-tab">
      <AlarmComponent />
    </div>
  );

  const renderFocus = () => (
    <div className="focus-tab">
      <FocusTimerComponent />
    </div>
  );

  const renderSmartScheduler = () => (
    <div className="smart-scheduler-tab">
      <SmartSchedulerComponent />
    </div>
  );

  const renderAIPlanner = () => (
    <div className="ai-planner-tab">
      <AIPlannerComponent />
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "home":
        return renderHome();
      case "timer":
        return renderTimer();
      case "stopwatch":
        return renderStopwatch();
      case "alarm":
        return renderAlarm();
      case "focus":
        return renderFocus();
      case "smartscheduler":
        return renderSmartScheduler();
      case "aiplanner":
        return renderAIPlanner();
      case "worldclock":
        return (
          <div className="world-clock-tab">
            <WorldClockComponent />
          </div>
        );
      default:
        return renderHome();
    }
  };

  return (
    <div className={`App ${isDarkMode ? 'dark-mode' : 'light-mode'}`} 
         style={{ 
             backgroundImage: backgroundImage,
             backgroundSize: 'cover',
             backgroundPosition: 'center',
             backgroundRepeat: 'no-repeat'
         }}>
      <div className={`sidebar ${isSidebarExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="profile-section">
            <div className="profile-icon">OM</div>
            {isSidebarExpanded && <span className="profile-name">One Matrix</span>}
          </div>
          <button className="toggle-button" onClick={toggleSidebar}>
            {isSidebarExpanded ? '<' : '>'}
          </button>
        </div>
        <div className="menu-items">
          <button 
            onClick={() => setActiveTab("home")} 
            className={activeTab === "home" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faHome} />
            {isSidebarExpanded && <span>Home</span>}
          </button>
          <button 
            onClick={() => setActiveTab("timer")} 
            className={activeTab === "timer" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faClock} />
            {isSidebarExpanded && <span>Timer</span>}
          </button>
          <button 
            onClick={() => setActiveTab("stopwatch")} 
            className={activeTab === "stopwatch" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faStopwatch} />
            {isSidebarExpanded && <span>Stopwatch</span>}
          </button>
          <button 
            onClick={() => setActiveTab("alarm")} 
            className={activeTab === "alarm" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faBell} />
            {isSidebarExpanded && <span>Alarm</span>}
          </button>
          <button 
            onClick={() => setActiveTab("focus")} 
            className={activeTab === "focus" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faHourglassHalf} />
            {isSidebarExpanded && <span>Focus</span>}
          </button>
          <button 
            onClick={() => setActiveTab("smartscheduler")} 
            className={activeTab === "smartscheduler" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faCalendar} />
            {isSidebarExpanded && <span>Smart Scheduler</span>}
          </button>
          <button 
            onClick={() => setActiveTab("aiplanner")} 
            className={activeTab === "aiplanner" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faRobot} />
            {isSidebarExpanded && <span>AI Planner</span>}
          </button>
          <button 
            onClick={() => setActiveTab("worldclock")} 
            className={activeTab === "worldclock" ? "active" : ""}
          >
            <FontAwesomeIcon icon={faGlobe} />
            {isSidebarExpanded && <span>World Clock</span>}
          </button>
        </div>
        <div className="sidebar-footer">
          <button className="dark-mode-toggle" onClick={toggleDarkMode}>
            <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} />
            {isSidebarExpanded && <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>}
          </button>
        </div>
      </div>

      <div className="sidebar-spacer"></div>

      <div className="content">
        {renderContent()}
      </div>

      <div className="widgets-container">
        <div className="weather-widget">
          <h3>Weather</h3>
          <div className="weather-info">
            {getWeatherIcon()}
            <p>{weatherData.temp}°C</p>
            <p>{weatherData.location}</p>  {/* Display location */}
          </div>
          <p>{weatherData.description}</p>
        </div>

        <div className="digital-clock-widget">
          {digitalClock.split("").map((digit, index) => (
            <span key={index} className="digit">
              {digit}
            </span>
          ))}
          <div className="svg-animation">
            {getCurrentSvg()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
